'use client';

import type { TDynamicData } from '../../types/dynamic-data';

import { fetchDynamicSectionsPaged } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ProjectCard } from './project-card';
import { Button } from '../ui/button';
import { Banner } from '../shared/banner';

export function ProjectsList({ locale }: { locale: string }) {
  const categories = ['Interiér', 'Rodinné domy'];

  const [selectedCategory, setSelectedCategory] = useState('Všetky');
  const tabsId = useId();
  const panelId = `${tabsId}-projects-panel`;
  const activeTabIndex =
    selectedCategory === 'Všetky' ? 0 : categories.indexOf(selectedCategory) + 1;

  const projectsQuery = useInfiniteQuery({
    queryKey: ['PROJECTS', locale, selectedCategory],
    queryFn: async ({ pageParam = 9 }) => {
      return await fetchDynamicSectionsPaged<TDynamicData>({
        locale,
        sectionIds: [6],
        page: pageParam,
        query: {
          results: 9,
          ...(selectedCategory === 'Všetky'
            ? {}
            : {
                valueFilters: [
                  {
                    fieldId: 16,
                    values: selectedCategory,
                  },
                ],
              }),
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const projects = projectsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <section className={cn('custom-section', 'py-20')}>
      <div
        className={cn(
          'custom-container--sm',
          'flex flex-col items-center justify-center gap-10 sm:gap-16 lg:gap-20',
          'ea-projects-list__container',
        )}
      >
        <Tabs
          defaultValue={selectedCategory}
          onValueChange={value => setSelectedCategory(value)}
          className="w-full flex-col items-start justify-start gap-10 sm:flex sm:gap-12"
        >
          <TabsList
            className={cn(
              'grid h-fit !w-full grid-cols-3 items-end justify-start gap-2 rounded-none bg-transparent p-0 sm:flex',
              'ea-projects-list__list',
            )}
          >
            <TabsTrigger
              id={`${tabsId}-tab-0`}
              aria-controls={panelId}
              value="Všetky"
              className={cn(
                'col-span-1 w-full rounded-none border-b-2 p-4 text-sm font-semibold shadow-none sm:w-fit sm:text-base lg:text-lg',
                'ea-projects-list__trigger',
              )}
            >
              Všetky
            </TabsTrigger>
            {categories.map((category, index) => (
              <TabsTrigger
                key={category}
                value={category}
                id={`${tabsId}-tab-${index + 1}`}
                aria-controls={panelId}
                className={cn(
                  'col-span-1 w-full rounded-none border-b-2 p-4 text-sm font-semibold shadow-none sm:w-fit sm:text-base lg:text-lg',
                  'ea-projects-list__trigger',
                )}
              >
                {category}
              </TabsTrigger>
            ))}
            <div className="hidden h-[2px] w-full bg-black/10 sm:block" />
          </TabsList>
          <TabsContent
            value={selectedCategory}
            id={panelId}
            aria-labelledby={`${tabsId}-tab-${activeTabIndex}`}
            className="m-0 w-full p-0"
          >
            {projectsQuery.error && <p className="pt-8">Vyskytla sa chyba.</p>}
            {projectsQuery.isSuccess && projects.length > 0 && (
              <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {projects.map((project: TDynamicData) => (
                  <ProjectCard project={project} key={project.slug} />
                ))}
              </div>
            )}
            <div
              className={cn('mt-8 flex w-full items-center justify-center', {
                hidden: !projectsQuery.hasNextPage,
              })}
            >
              <Button
                onClick={() => projectsQuery.fetchNextPage()}
                disabled={projectsQuery.isFetchingNextPage}
                variant={'dark'}
                className="w-full sm:w-fit"
              >
                Zobraziť viac
              </Button>
            </div>
          </TabsContent>
        </Tabs>
        <Banner
          title="Chcem získať cenovú ponuku pre môj projekt"
          url="/cennik"
          className={{
            container: 'bg-primary',
            title: 'w-full max-w-[700px]',
            image: 'mix-blend-color-burn',
          }}
        />
      </div>
    </section>
  );
}
